/**
 * The page-help registry's context and hooks (components in `page-help.tsx`):
 * `HelpDocumentation` registers through `usePageHelpRegistration`, `PageHeader`
 * announces itself through `usePageHelpHeader`.
 */
import { createContext, useContext, useId, useLayoutEffect, type ReactNode } from 'react';

export interface PageHelpEntry {
  title: string;
  description?: string;
  content: ReactNode;
  learnMoreSlug?: string;
}

export interface PageHelpContextValue {
  /** Registered help sections, in mount order (ids only; content lives in `entries`). */
  ids: string[];
  entries: React.MutableRefObject<Map<string, PageHelpEntry>>;
  register: (id: string) => () => void;
  /** How many `PageHeader`s are mounted; help shows in the header only when one is. */
  headerCount: number;
  registerHeader: () => () => void;
}

export const PageHelpContext = createContext<PageHelpContextValue | null>(null);

/**
 * For `HelpDocumentation`: registers this section with the page header and
 * returns true, or returns false when it should render inline (no provider,
 * no header on screen, or `inline` asked for). Layout effects, so the switch
 * from inline to header happens before the first paint.
 */
export function usePageHelpRegistration(entry: PageHelpEntry, inline: boolean): boolean {
  const ctx = useContext(PageHelpContext);
  const id = useId();
  const inHeader = !inline && !!ctx && ctx.headerCount > 0;
  // The latest content, every render — read by the sheet, never stored in state.
  if (ctx && inHeader) ctx.entries.current.set(id, entry);
  const register = ctx?.register;
  useLayoutEffect(() => {
    if (!register || !inHeader) return;
    return register(id);
  }, [register, inHeader, id]);
  return inHeader;
}

/** For `PageHeader`: announces a header, so help moves into it. */
export function usePageHelpHeader(): void {
  const registerHeader = useContext(PageHelpContext)?.registerHeader;
  useLayoutEffect(() => registerHeader?.(), [registerHeader]);
}

